import { UseMutateAsyncFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback, useEffect } from 'react';
import { createBulkStudioVideo, createDraftStudioVideo, createStudioVideo, listStudioVideos } from '@/api/studio-video';
import { useCurrentApp, useUserDataStore } from '@/stores/userDataStore';
import { StudioVideo, StudioVideoStatus } from '@/features/ai-video-creator/stores/studioVideoStore';
import {
  LibraryNotification,
  useLibraryNotificationsStore,
} from '@/features/notifications/stores/libraryNotificationsStore';
import { ICreateStudioVideoPayload, IStudioVideoMetadata } from '../types';
import { useToast } from '@/hooks/useToast';
import { useShallow } from 'zustand/react/shallow';
export const STUDIO_VIDEOS_QUERY_KEY = 'getStudioVideos';

type UseQueryStudioVideosProps = {
  enabled?: boolean;
  statuses?: StudioVideoStatus[];
};

export const useQueryStudioVideos = ({ statuses }: UseQueryStudioVideosProps = {}): {
  studioVideos: StudioVideo[];
  isLoading: boolean;
} => {
  const { appKey, isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();
  const { setNotifications } = useLibraryNotificationsStore(
    useShallow((state) => ({
      setNotifications: state.setNotifications,
    }))
  );

  const { data, isLoading } = useQuery({
    queryKey: [STUDIO_VIDEOS_QUERY_KEY, appKey],
    queryFn: async () => listStudioVideos(appKey),
    enabled: !!appKey && isLoggedIn,
  });

  const { notFailedStudioVideos, failedStudioVideos } = useMemo(() => {
    if (!data) return { notFailedStudioVideos: [], failedStudioVideos: [] };
    const notFailedStudioVideos: StudioVideo[] = [];
    const failedStudioVideos: StudioVideo[] = [];
    data.forEach((video) => {
      if (statuses) {
        if (statuses.includes(video.status!)) {
          notFailedStudioVideos.push(video);
        } else {
          failedStudioVideos.push(video);
        }
        return;
      }
      if (video.status === StudioVideoStatus.FAILED) {
        failedStudioVideos.push(video);
        return;
      }
      notFailedStudioVideos.push(video);
    });

    return { notFailedStudioVideos, failedStudioVideos };
  }, [data, statuses]);

  const notifyAboutFailedItems = useCallback(
    (videos: StudioVideo[]) => {
      const notifications: LibraryNotification[] = [];
      videos.forEach((video) => {
        notifications.push({
          id: video.id || 'pupupu',
          source: 'studio_video',
          title: 'Failed to generate Studio Video',
          description: video.name || 'Ai Video',
          tone: 'critical',
          toolUsed: 'studio_video',
          generatedAt: video.createdAt || new Date().toISOString(),
          error: video.error,
        });
      });
      setNotifications(notifications);
    },
    [setNotifications]
  );

  useEffect(() => {
    notifyAboutFailedItems(failedStudioVideos);
  }, [failedStudioVideos, notifyAboutFailedItems]);
  return { studioVideos: notFailedStudioVideos, isLoading: isLoadingUser || isLoading };
};

export const useStudioVideoCache = (): {
  invalidateStudioVideosListQuery: () => void;
  updateStudioVideoItemCache: (updatedVideo: StudioVideo) => void;
} => {
  const queryClient = useQueryClient();

  const invalidateStudioVideosListQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [STUDIO_VIDEOS_QUERY_KEY] });
  }, [queryClient]);

  const updateStudioVideoItemCache = useCallback(
    (updatedVideo: StudioVideo) => {
      queryClient.setQueryData<StudioVideo[]>([STUDIO_VIDEOS_QUERY_KEY, updatedVideo.appKey], (oldVideos) => {
        if (!oldVideos) return oldVideos;

        return oldVideos.map((video) => (video.id === updatedVideo.id ? { ...video, ...updatedVideo } : video));
      });
    },
    [queryClient]
  );

  return { invalidateStudioVideosListQuery, updateStudioVideoItemCache };
};

export const useCreateStudioVideo = ({ onDraftError }: { onDraftError?: () => void } = {}): {
  createStudioVideo: UseMutateAsyncFunction<
    { video: ICreateStudioVideoPayload; metadata?: IStudioVideoMetadata },
    Error,
    { video: ICreateStudioVideoPayload; metadata?: IStudioVideoMetadata }
  >;
  createDraftStudioVideo: UseMutateAsyncFunction<StudioVideo, Error, StudioVideo>;
  createBulkStudioVideo: UseMutateAsyncFunction<
    { videos: ICreateStudioVideoPayload[]; metadata: IStudioVideoMetadata },
    Error,
    { videos: ICreateStudioVideoPayload[]; metadata: IStudioVideoMetadata }
  >;
} => {
  const { invalidateStudioVideosListQuery } = useStudioVideoCache();
  const { notify, notifyError } = useToast();
  const queryClient = useQueryClient();
  const createStudioVideoMutation = useMutation({
    mutationFn: async (body: { video: ICreateStudioVideoPayload; metadata?: IStudioVideoMetadata }) => {
      queryClient.setQueryData([STUDIO_VIDEOS_QUERY_KEY, body.video.studioVideo.appKey], (oldVideos: StudioVideo[]) => {
        if (!oldVideos) return [body.video.studioVideo];
        return [...oldVideos, body.video.studioVideo];
      }); // optimistic update
      return await createStudioVideo(body);
    },
    onError: () => {
      invalidateStudioVideosListQuery();
    },
  });

  const createBulkStudioVideoMutation = useMutation({
    mutationFn: async (body: { videos: ICreateStudioVideoPayload[]; metadata: IStudioVideoMetadata }) =>
      await createBulkStudioVideo(body),
    onSuccess: () => {
      invalidateStudioVideosListQuery();
    },
  });

  const createDraftStudioVideoMutation = useMutation({
    mutationFn: async (body: StudioVideo) => {
      queryClient.setQueryData([STUDIO_VIDEOS_QUERY_KEY, body.appKey], (oldVideos: StudioVideo[]) => {
        if (!oldVideos) return [body];
        return [...oldVideos, body];
      }); // optimistic update
      return await createDraftStudioVideo(body);
    },
    onSuccess: () => {
      notify('Draft saved');
    },
    onError: () => {
      invalidateStudioVideosListQuery();
      onDraftError?.();
    },
  });

  return {
    createStudioVideo: createStudioVideoMutation.mutateAsync,
    createDraftStudioVideo: createDraftStudioVideoMutation.mutateAsync,
    createBulkStudioVideo: createBulkStudioVideoMutation.mutateAsync,
  };
};

export const useStudioVideosPrefetch = () => {
  const { appKey } = useCurrentApp();
  const { isLoggedIn } = useUserDataStore();
  const queryClient = useQueryClient();
  const prefetchStudioVideos = useCallback(() => {
    if (appKey && isLoggedIn) {
      queryClient.prefetchQuery({
        queryKey: [STUDIO_VIDEOS_QUERY_KEY, appKey],
        queryFn: () => listStudioVideos(appKey),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [appKey, isLoggedIn, queryClient]);

  useEffect(() => {
    prefetchStudioVideos();
  }, [prefetchStudioVideos]);

  return prefetchStudioVideos;
};
